/**
 * What the app is building next, as the people who use it read it (`D263`,
 * `D264`) — the ONE curated source behind the app's "O que vem por aí" sheet,
 * the votes the API accepts, the owner's digest and the site's `/roadmap`.
 *
 * ── HOW TO EDIT THIS FILE ───────────────────────────────────────────────────
 * The owner edits it and ships; there is no admin screen. A new item is a new
 * entry with a NEW `id`: the id is what a vote is stored under, so renaming
 * one orphans every vote it had, and re-using one hands them to another idea.
 * Moving an item between statuses is editing `status`; once it ships, set
 * `status: "shipped"` and `shippedIn` to the release that carried it.
 *
 * Plain language, both languages, nothing internal: no decision or trap code,
 * no file, no product this one was compared with, no price.
 * `community-content.test.ts` refuses the shapes of those it can recognise.
 * Nothing here is a promise of a date, and nothing says who suggested what.
 *
 * ── WHAT A STATUS MEANS ─────────────────────────────────────────────────────
 * - `building` — being built now.
 * - `planned` — decided, not started. Votes say what comes first.
 * - `considering` — not decided. Votes say whether it is wanted.
 * - `shipped` — in the app, since `shippedIn`.
 *
 * Only `planned` and `considering` take votes ({@link isVotableRoadmapItem}):
 * a vote on something already being built, or already out, informs nothing.
 */
export const ROADMAP_STATUSES = [
    "building",
    "planned",
    "considering",
    "shipped",
];
/** The statuses a person may vote on. */
export const VOTABLE_ROADMAP_STATUSES = [
    "planned",
    "considering",
];
/** Lowercase words joined by hyphens, as a vote is stored under. */
export const ROADMAP_ITEM_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const ROADMAP_ITEM_ID_MAX = 48;
export const ROADMAP_ITEMS = [
    // ── Planned ──────────────────────────────────────────────────────────────
    {
        description: {
            "en-US": "Log and read every load in pounds, with pound plates in the plate calculator.",
            "pt-BR": "Registrar e ver as cargas em libras, com anilhas em libras na calculadora.",
        },
        id: "pounds",
        status: "planned",
        title: {
            "en-US": "Pounds as well as kilograms",
            "pt-BR": "Libras (lb) além de quilos",
        },
    },
    {
        description: {
            "en-US": "The whole app, the trainer included, in these two languages.",
            "pt-BR": "O app inteiro, com o treinador, nesses dois idiomas.",
        },
        id: "spanish-portugal",
        status: "planned",
        title: {
            "en-US": "Spanish and European Portuguese",
            "pt-BR": "Espanhol e português de Portugal",
        },
    },
    {
        description: {
            "en-US": "Invite your clients, build and assign their plans, and follow each one's workouts and measurements.",
            "pt-BR": "Convidar alunos, montar e atribuir planos e acompanhar os treinos e as medidas de cada um.",
        },
        id: "trainer-tools",
        status: "planned",
        title: {
            "en-US": "Tools for personal trainers",
            "pt-BR": "Ferramentas para personal trainers",
        },
    },
    {
        description: {
            "en-US": "Send your plan to someone; whoever opens it reviews every exercise before taking it on.",
            "pt-BR": "Mandar seu plano para alguém; quem abre revisa cada exercício antes de adotar.",
        },
        id: "plan-link",
        status: "planned",
        title: {
            "en-US": "Share a plan by link",
            "pt-BR": "Compartilhar um plano por link",
        },
    },
    {
        description: {
            "en-US": "Challenges and clubs with friends, with a leaderboard only if the group wants one.",
            "pt-BR": "Desafios e clubes com amigos, com ranking só se o grupo quiser.",
        },
        id: "group-challenges",
        status: "planned",
        title: {
            "en-US": "Group challenges",
            "pt-BR": "Desafios em grupo",
        },
    },
    {
        description: {
            "en-US": "Today's workout, your week and your muscles' recovery, without opening the app.",
            "pt-BR": "O treino de hoje, a semana e a recuperação dos músculos sem abrir o app.",
        },
        id: "home-widgets",
        status: "planned",
        title: {
            "en-US": "Home screen widgets",
            "pt-BR": "Widgets na tela inicial",
        },
    },
    {
        description: {
            "en-US": "Your workouts in Android's Health Connect.",
            "pt-BR": "Seus treinos no Health Connect do Android.",
        },
        id: "health-connect",
        status: "planned",
        title: {
            "en-US": "Health Connect",
            "pt-BR": "Health Connect",
        },
    },
    {
        description: {
            "en-US": "Post your workouts to Strava.",
            "pt-BR": "Publicar seus treinos no Strava.",
        },
        id: "strava",
        status: "planned",
        title: {
            "en-US": "Strava",
            "pt-BR": "Strava",
        },
    },
    {
        description: {
            "en-US": "The rest countdown in a notification, with finish set and skip rest, also on your watch.",
            "pt-BR": "A contagem do descanso numa notificação, com concluir série e pular descanso, também no relógio.",
        },
        id: "lock-screen-rest",
        status: "planned",
        title: {
            "en-US": "Rest timer on the lock screen",
            "pt-BR": "Descanso na tela bloqueada",
        },
    },
    // ── Under consideration ──────────────────────────────────────────────────
    {
        description: {
            "en-US": "Hear the next set, its load and the rest without looking at your phone.",
            "pt-BR": "Ouvir a próxima série, a carga e o descanso sem olhar para o celular.",
        },
        id: "voice-coach",
        status: "considering",
        title: {
            "en-US": "A voice coach during the workout",
            "pt-BR": "Treinador por voz durante o treino",
        },
    },
    {
        description: {
            "en-US": "Bodyweight and low-equipment workouts, with filters such as no jumping.",
            "pt-BR": "Treinos com o peso do corpo e pouco equipamento, com filtros como sem saltos.",
        },
        id: "home-mode",
        status: "considering",
        title: {
            "en-US": "Home mode",
            "pt-BR": "Modo casa",
        },
    },
    {
        description: {
            "en-US": "Short programmes with one clear goal, mobility included.",
            "pt-BR": "Programas curtos com uma meta clara, incluindo mobilidade.",
        },
        id: "month-challenges",
        status: "considering",
        title: {
            "en-US": "28-day challenges",
            "pt-BR": "Desafios de 28 dias",
        },
    },
    {
        description: {
            "en-US": "French, German, Italian and more, after Spanish.",
            "pt-BR": "Francês, alemão, italiano e outros, depois do espanhol.",
        },
        id: "more-languages",
        status: "considering",
        title: {
            "en-US": "More languages",
            "pt-BR": "Mais idiomas",
        },
    },
    {
        description: {
            "en-US": "Check and adjust your training from the AI assistant you already use.",
            "pt-BR": "Consultar e ajustar seu treino pelo assistente de IA que você já usa.",
        },
        id: "ai-assistants",
        status: "considering",
        title: {
            "en-US": "Your trainer in other AI assistants",
            "pt-BR": "O treinador em outros assistentes de IA",
        },
    },
    {
        description: {
            "en-US": "Eating suggestions tied to your goal and your training.",
            "pt-BR": "Sugestões de alimentação ligadas ao seu objetivo e ao seu treino.",
        },
        id: "meal-plan",
        status: "considering",
        title: {
            "en-US": "Meal plan",
            "pt-BR": "Plano alimentar",
        },
    },
    // ── Shipped ──────────────────────────────────────────────────────────────
    {
        description: {
            "en-US": "Ready-made cards to share your records, your streak, your milestones and your recap.",
            "pt-BR": "Cartões prontos para compartilhar seus recordes, sua sequência, seus marcos e a Retrospectiva.",
        },
        id: "share-cards",
        shippedIn: "2.21.0",
        status: "shipped",
        title: {
            "en-US": "Share your achievements",
            "pt-BR": "Compartilhar suas conquistas",
        },
    },
    {
        description: {
            "en-US": "Your weekly or monthly recap as a short video, ready to post as a story.",
            "pt-BR": "A Retrospectiva da semana ou do mês como um vídeo curto, pronto para postar nos stories.",
        },
        id: "recap-video",
        shippedIn: "2.21.0",
        status: "shipped",
        title: {
            "en-US": "Your recap as a short video",
            "pt-BR": "Retrospectiva em vídeo",
        },
    },
    {
        description: {
            "en-US": "Add an exercise to the workout in progress, or start a blank workout and pick as you go.",
            "pt-BR": "Adicionar um exercício ao treino em andamento, ou começar um treino em branco e escolher pelo caminho.",
        },
        id: "add-exercise-mid-workout",
        shippedIn: "2.20.0",
        status: "shipped",
        title: {
            "en-US": "Add exercises during a workout",
            "pt-BR": "Adicionar exercícios durante o treino",
        },
    },
    {
        description: {
            "en-US": "Say how many minutes you have and get a workout for today from what has recovered.",
            "pt-BR": "Diga quantos minutos você tem e receba um treino para hoje com o que está recuperado.",
        },
        id: "quick-workout",
        shippedIn: "2.19.0",
        status: "shipped",
        title: {
            "en-US": "Quick workout",
            "pt-BR": "Treino rápido",
        },
    },
    {
        description: {
            "en-US": "Exercises done back to back in rounds, with the rest after the round.",
            "pt-BR": "Exercícios feitos em sequência, em rodadas, com o descanso depois da rodada.",
        },
        id: "supersets",
        shippedIn: "2.19.0",
        status: "shipped",
        title: {
            "en-US": "Supersets",
            "pt-BR": "Supersets",
        },
    },
    {
        description: {
            "en-US": "How your week or month went: sets per muscle, what is progressing and your consistency.",
            "pt-BR": "Como foi a semana ou o mês: séries por músculo, o que está evoluindo e a sua constância.",
        },
        id: "progress-reports",
        shippedIn: "2.19.0",
        status: "shipped",
        title: {
            "en-US": "Weekly and monthly reports",
            "pt-BR": "Relatório semanal e mensal",
        },
    },
];
const BY_ID = new Map(ROADMAP_ITEMS.map((item) => [item.id, item]));
/** The item of that id, or `undefined` for an id this build does not know. */
export function roadmapItem(id) {
    return BY_ID.get(id);
}
/** Whether a person may vote on `id` now: known, and planned or considered. */
export function isVotableRoadmapItem(id) {
    const item = BY_ID.get(id);
    return item !== undefined && VOTABLE_ROADMAP_STATUSES.includes(item.status);
}
//# sourceMappingURL=roadmap.js.map